// Legio2Timeline.jsx
import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

// Basic Leaflet marker icon fix (so markers show correctly in many bundlers)
const defaultIcon = new L.Icon({
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -34],
  shadowSize: [41, 41],
});

// --- DATA: Timeline phases and locations --- //

const legio2Phases = [
  {
    id: "cantabrian",
    label: "Cantabrian Wars",
    years: "25–19 BC",
    summary: `
Legio II is forged in the mountains of northern Spain, fighting Rome’s last great tribal war in Hispania. 
Here the future II Augusta learns how to campaign in wet uplands, how to break hill strongholds, and how to 
live for months in timber forts on cold ridges. This experience will echo later in Britain.
    `,
    locations: [
      // Context phase – no UK markers, but you could add Acci (Guadix) later if you do a full Empire map
    ],
  },
  {
    id: "invasion",
    label: "Invasion & Early Forts",
    years: "AD 43–55",
    summary: `
Chosen as one of four invasion legions, II Augusta sails for Britannia under Titus Flavius Vespasianus. 
They likely land near Rutupiae (Richborough), then push west through the Durotriges and Dumnonii, 
assaulting hillforts and securing harbours. The men start to bind their eagle and the Capricorn standard 
to this new, rain-soaked island.
    `,
    locations: [
      {
        id: "richborough",
        name: "Rutupiae (Richborough)",
        role: "Invasion bridgehead and later Saxon Shore fort",
        lat: 51.2924,
        lng: 1.3244,
        text: `
Probable landing and supply port for the Claudian invasion, starting point of Watling Street and gateway to Britannia.
        `,
      },
      {
        id: "alchester",
        name: "Alchester legionary fortress",
        role: "Early fortress behind the invasion front",
        lat: 51.8765,
        lng: -1.1678,
        text: `
Timber-built legionary base for II Augusta, established in the mid-40s AD at a key crossroads in central Britain.
Gate timbers dated to AD 44–45 anchor this early presence.
        `,
      },
      {
        id: "waddon",
        name: "Waddon Hill (Dorset)",
        role: "Forward strongpoint in Vespasian’s campaign",
        lat: 50.797,
        lng: -2.753, // approximate; you can tweak
        text: `
A fortified position in the Durotriges heartland; part of the chain of operations where II Augusta broke local resistance.
        `,
      },
    ],
  },
  {
    id: "southwest",
    label: "South-West Consolidation",
    years: "AD 55–74",
    summary: `
With the south largely subdued, II Augusta settles into fortress life in the west. First at Isca Dumnoniorum (Exeter), 
then probably at Glevum (Gloucester), the legion watches roads, collects taxes, and trains for future wars. 
The memory of Boudica’s revolt and Poenius Postumus’ fatal hesitation hangs over the unit like a warning.
    `,
    locations: [
      {
        id: "exeter",
        name: "Isca Dumnoniorum (Exeter)",
        role: "Legionary fortress and later civitas capital",
        lat: 50.722,
        lng: -3.534,
        text: `
Legionary base for about two decades. Around the fortress, a town grows that will outlive the military, 
its walls still tracing the Roman footprint.
        `,
      },
      {
        id: "glevum",
        name: "Glevum (Gloucester)",
        role: "Probable legionary base, later colonia",
        lat: 51.867,
        lng: -2.249,
        text: `
A base on the Severn corridor. Later promoted to a colonia, Glevum becomes a retirement town for veterans, 
including men of II Augusta who lay down their shields but keep their stories.
        `,
      },
    ],
  },
  {
    id: "caerleon",
    label: "Caerleon & Welsh Marches",
    years: "AD 74–c. 208",
    summary: `
Isca Augusta (Caerleon) becomes the long-term home of Legio II Augusta. 
Here the eagle lives in stone: principia, baths, barracks, amphitheatre. From this base the legion watches the Silures, 
sends detachments to Ireland-facing coasts, and offers sacrifice to Jupiter, Mars, and local river gods. 
For generations, recruits from Britain and the continent swear their oaths within these walls.
    `,
    locations: [
      {
        id: "caerleon",
        name: "Isca Augusta (Caerleon)",
        role: "Permanent legionary fortress",
        lat: 51.6103,
        lng: -2.9589,
        text: `
Major base of II Augusta for over a century. Amphitheatre, baths, and barracks form a complete military landscape 
where thousands of soldiers live, train, and worship.
        `,
      },
      // Later you can add marching camps in the Usk valley and Brecon Beacons as extra points
    ],
  },
  {
    id: "antonine",
    label: "Antonine Wall & Northern Frontiers",
    years: "2nd century",
    summary: `
Detachments from II Augusta march north to help build and hold the Antonine Wall. 
On distance slabs like the one at Bridgeness, the legion carves its name into stone beside the emperor’s titles, 
linking its identity to the very edge of the Roman world. These stones are both engineering markers and ritual statements 
that the gods and the emperor see this line.
    `,
    locations: [
      {
        id: "bridgeness",
        name: "Bridgeness (Antonine Wall distance slab)",
        role: "Eastern terminus marker of Antonine Wall section built by II Augusta",
        lat: 56.0136,
        lng: -3.5931,
        text: `
Findspot of the famous distance slab recording a stretch of the Antonine Wall built by II Augusta. 
A physical signature of the legion on the northern frontier.
        `,
      },
    ],
  },
  {
    id: "severan",
    label: "Severan Campaigns",
    years: "late 2nd–early 3rd c.",
    summary: `
In the civil war of AD 196, II Augusta backs Clodius Albinus and marches to Lugdunum. 
After his defeat and the chaos on the frontier, Septimius Severus comes to Britain in person. 
II Augusta is stationed at Carpow on the Tay estuary, tied into a supply network of ships and granaries as the emperor 
pushes deep into Caledonian lands. The campaigns are brutal, cold, and spiritually exhausting.
    `,
    locations: [
      {
        id: "carpow",
        name: "Carpow Roman fortress",
        role: "Severan supply and operations base",
        lat: 56.3449,
        lng: -3.2804,
        text: `
A stone-built fortress on the Tay, occupied by II Augusta and VI Victrix. 
Inscriptions and tiles preserve the legion’s name beside the cold northern tide.
        `,
      },
    ],
  },
  {
    id: "late",
    label: "Late Roman Phase",
    years: "3rd–4th century",
    summary: `
As the imperial system changes, II Augusta shrinks into detachments, redeploys, and is finally recorded at Rutupiae again 
as part of the Saxon Shore defence. The old Caerleon fortress falls quiet. 
The legion’s standards may be broken up or carried away, but its long imprint remains in forts, 
roads, and veterans’ farmsteads across western Britain.
    `,
    locations: [
      {
        id: "caerleon-late",
        name: "Isca Augusta (Caerleon) – later phase",
        role: "Former base, partly abandoned",
        lat: 51.6103,
        lng: -2.9589,
        text: `
By the later 3rd century many buildings are reused or abandoned, but Caerleon’s plan still marks where II Augusta once lived.
        `,
      },
      {
        id: "richborough-late",
        name: "Rutupiae (Richborough) – late phase",
        role: "Saxon Shore fort with II Augusta garrison",
        lat: 51.2924,
        lng: 1.3244,
        text: `
In the Notitia Dignitatum, II Augusta appears here as a coastal garrison, guarding the sea routes it once used for invasion.
        `,
      },
    ],
  },
];

// --- COMPONENT --- //

const Legio2Timeline = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const activePhase = legio2Phases[activeIndex];

  // Autoplay: advance every 7 seconds when playing
  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % legio2Phases.length);
    }, 7000);

    return () => clearInterval(timer);
  }, [isPlaying]);

  // Choose a map centre for the current phase
  const currentLocations = activePhase.locations;
  const defaultCenter = [54, -2.5]; // general Britain
  const mapCenter =
    currentLocations && currentLocations.length > 0
      ? [currentLocations[0].lat, currentLocations[0].lng]
      : defaultCenter;

  return (
    <div className="legio2-wrapper">
      <h1 className="legio2-title">Legio II Augusta – Timeline & Map</h1>

      <div className="legio2-layout">
        {/* LEFT PANE: Text */}
        <div className="legio2-left">
          <div className="legio2-phase-header">
            <h2>{activePhase.label}</h2>
            <p className="legio2-years">{activePhase.years}</p>
          </div>

          <div className="legio2-summary">
            {activePhase.summary
              .trim()
              .split("\n")
              .map((line, idx) => (
                <p key={idx}>{line.trim()}</p>
              ))}
          </div>

          <div className="legio2-locations">
            <h3>Key Locations in this Phase</h3>
            {currentLocations.length === 0 && (
              <p className="legio2-muted">
                No British map points for this phase yet – the story is still
                on the continent.
              </p>
            )}
            {currentLocations.map((loc) => (
              <div key={loc.id} className="legio2-location-card">
                <strong>{loc.name}</strong>
                <div className="legio2-location-role">{loc.role}</div>
                <p>
                  {loc.text
                    .trim()
                    .split("\n")
                    .map((line, idx) => (
                      <span key={idx}>
                        {line.trim()}
                        <br />
                      </span>
                    ))}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT PANE: Map */}
        <div className="legio2-right">
          <MapContainer
            key={activePhase.id} // forces recenter on phase change
            center={mapCenter}
            zoom={6}
            style={{ width: "100%", height: "100%" }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {currentLocations.map((loc) => (
              <Marker
                key={loc.id}
                position={[loc.lat, loc.lng]}
                icon={defaultIcon}
              >
                <Popup>
                  <strong>{loc.name}</strong>
                  <br />
                  <em>{loc.role}</em>
                  <br />
                  {loc.text}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* TIMELINE CONTROLS */}
      <div className="legio2-timeline-controls">
        <button
          className="legio2-playpause"
          onClick={() => setIsPlaying((prev) => !prev)}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>

        <div className="legio2-phase-buttons">
          {legio2Phases.map((phase, idx) => (
            <button
              key={phase.id}
              className={
                idx === activeIndex
                  ? "legio2-phase-button active"
                  : "legio2-phase-button"
              }
              onClick={() => {
                setActiveIndex(idx);
                setIsPlaying(false); // stop autoplay when user manually picks
              }}
            >
              <span className="legio2-phase-label">{phase.label}</span>
              <span className="legio2-phase-years">{phase.years}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Legio2Timeline;
